import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Check, Waves } from "lucide-react";
import { toast } from "sonner";

export default function Survey() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api
      .get(`/surveys/${token}`)
      .then((r) => {
        setState({ loading: false, error: "", data: r.data });
        if (r.data.submitted) {
          setRating(r.data.rating || 0);
          setComment(r.data.comment || "");
          setDone(true);
        }
      })
      .catch((err) => setState({ loading: false, error: apiError(err, "Survey not found"), data: null }));
  }, [token]);

  const submit = async () => {
    if (!rating) {
      toast.error("Pop a star rating in first please");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/surveys/${token}`, { rating, comment });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-slate-400">Loading…</div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="ns-card p-10 max-w-md text-center">
          <div className="font-serif text-2xl text-slate-900 mb-2">Hmm, that link isn&apos;t valid</div>
          <p className="text-sm text-slate-500">{state.error}</p>
        </div>
      </div>
    );
  }

  const d = state.data || {};
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-full bg-teal-700 text-white flex items-center justify-center">
            <Waves className="w-5 h-5" />
          </div>
          <div>
            <div className="ns-label text-teal-700">Newport Stays</div>
            <div className="font-serif text-2xl text-slate-900">Your quick review</div>
          </div>
        </div>

        {done ? (
          <div className="ns-card p-10 text-center" data-testid="survey-done">
            <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-700 mx-auto flex items-center justify-center">
              <Check className="w-7 h-7" />
            </div>
            <div className="font-serif text-2xl mt-4 text-slate-900">Thank you, {d.guest_first_name || "friend"}</div>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              We&apos;ve logged your review of <strong>{d.property_name}</strong>. If you gave us less than 5 stars,
              a Newport Stays team member will be in touch to make it right.
            </p>
            <div className="flex justify-center mt-6 gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  data-testid={`survey-done-star-${n}`}
                  className={`w-6 h-6 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`}
                />
              ))}
            </div>
            {comment && (
              <p className="mt-4 text-sm text-slate-600 italic max-w-lg mx-auto">&ldquo;{comment}&rdquo;</p>
            )}
          </div>
        ) : (
          <div className="ns-card p-10" data-testid="survey-form">
            <div className="text-sm text-slate-500 mb-1">Hi {d.guest_first_name || "there"},</div>
            <div className="font-serif text-3xl text-slate-900 leading-tight">
              How was your stay at{" "}
              <span className="text-teal-700">{d.property_name || "our home"}</span>?
            </div>
            {d.check_in && d.check_out && (
              <div className="ns-label mt-2">
                {d.check_in} → {d.check_out}
              </div>
            )}
            <div className="ns-hairline my-8" />

            <div className="text-sm text-slate-600 mb-3">Give us a star rating</div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`survey-star-${n}`}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n)}
                  className="p-1 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
                  aria-label={`${n} stars`}
                >
                  <Star
                    className={`w-10 h-10 transition-colors ${
                      n <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-slate-200"
                    }`}
                  />
                </button>
              ))}
            </div>

            <div className="text-sm text-slate-600 mt-8 mb-3">
              Anything we could have done better? <span className="text-slate-400">(optional)</span>
            </div>
            <Textarea
              data-testid="survey-comment"
              rows={4}
              placeholder="e.g. the coffee machine ran out of pods on day two…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="bg-white"
            />

            <Button
              data-testid="survey-submit"
              disabled={submitting}
              onClick={submit}
              className="mt-6 rounded-full ns-btn-primary h-12 px-8"
            >
              {submitting ? "Sending…" : "Send my review"}
            </Button>
            <p className="mt-4 text-[11px] text-slate-400">
              Your feedback goes to Newport Stays only — it is never posted publicly.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
